'use client'

import { useRouter, useParams } from 'next/navigation'
import { useState, useCallback, useMemo } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { type FilterType } from '../../../../categoryConfig'
import {
  parseUrlFilters,
  buildFilterUrl,
  parseProductTypeFilterUrlKeys,
  canonicalProductTypeFilterSegment,
  canonicalProductTypeFilterSegmentFromSelection,
  formatProductTypeFilterSummary,
  type ProductTypeUrlKey,
} from '../helpers'
import { tracking } from '@/tracking'

const VALID_PRODUCT_TYPE_KEYS = [
  'apps',
  'books',
  'downloads',
  'ebooks',
  'tricks',
  'dvds',
] as const satisfies readonly ProductTypeUrlKey[]

export const CategoryFilter = ({
  categoryName = 'products',
  totalProducts,
  filters = [],
  showProductTypeInFilter = true,
  showStockInFilter = true,
  productTypeInFilter,
}: CategoryFilterProps) => {
  const params = useParams()
  const router = useRouter()
  const slugs = params.slugs as string[]
  const categorySlug = params.categorySlug as string

  const parsedFilters = useMemo(
    () => parseUrlFilters(slugs, categorySlug),
    [slugs, categorySlug]
  )
  const {
    filters: currentFilters,
    sort: currentSort,
    inStock,
    onSale,
    exclusive,
  } = parsedFilters

  const [sort, setSort] = useState<string>(currentSort)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const productTypeParam = params.productTypeFilter as string
  const canonicalProductSegment = useMemo(
    () => canonicalProductTypeFilterSegment(productTypeParam),
    [productTypeParam]
  )

  const selectedKeys = useMemo(
    () => parseProductTypeFilterUrlKeys(productTypeParam),
    [productTypeParam]
  )

  const normalizedProductTypeInFilter = VALID_PRODUCT_TYPE_KEYS.includes(
    productTypeInFilter as (typeof VALID_PRODUCT_TYPE_KEYS)[number]
  )
    ? (productTypeInFilter as (typeof VALID_PRODUCT_TYPE_KEYS)[number])
    : undefined

  const enabledProductTypes = useMemo((): ProductTypeUrlKey[] => {
    if (normalizedProductTypeInFilter != null) {
      return [normalizedProductTypeInFilter]
    }
    const configured = VALID_PRODUCT_TYPE_KEYS.filter(productType =>
      filters.includes(productType as FilterType)
    )
    if (configured.length > 0) {
      return [...configured]
    }
    return [...VALID_PRODUCT_TYPE_KEYS]
  }, [normalizedProductTypeInFilter, filters])

  const allowFilterProductType =
    showProductTypeInFilter && enabledProductTypes.length > 0

  const isAllProductTypes = canonicalProductSegment === 'all'

  /** Sidebar copy: never list multiple selected types; use plain "products". */
  const sidebarProductLabel = useMemo(() => {
    if (selectedKeys.length > 1) {
      return 'products'
    }
    return formatProductTypeFilterSummary(canonicalProductSegment)
  }, [selectedKeys.length, canonicalProductSegment])

  const navigateWithDebounce = useCallback(
    (newUrl: string) => {
      setIsUpdating(true)
      router.push(newUrl)
    },
    [router]
  )

  const trackFilteredCategory = useCallback(
    (overrides?: {
      filters?: string[]
      sort?: string
      inStock?: boolean
      onSale?: boolean
      exclusive?: boolean
      productTypeSegment?: string
    }) => {
      const f = overrides?.filters ?? currentFilters
      const s = overrides?.sort ?? sort
      const stock = overrides?.inStock ?? inStock
      const sale = overrides?.onSale ?? onSale
      const excl = overrides?.exclusive ?? exclusive
      const segment = overrides?.productTypeSegment ?? canonicalProductSegment
      tracking.track('Filtered category', {
        Category: categoryName,
        'Filter type': formatProductTypeFilterSummary(segment),
        'Filter out of stock': stock,
        'Filter on sale': sale,
        'Filter exclusives': excl,
        'Filter sort method': s,
        'Active filters': f.join(',') || 'none',
        'Filter product types': segment,
      })
    },
    [
      categoryName,
      canonicalProductSegment,
      currentFilters,
      sort,
      inStock,
      onSale,
      exclusive,
    ]
  )

  const addToRoute = useCallback(
    (value: string) => {
      const newFilters = [...currentFilters]
      if (!newFilters.includes(value)) {
        newFilters.push(value)
      }

      const newUrl = buildFilterUrl(
        params.categorySlug as string,
        canonicalProductSegment,
        newFilters,
        sort,
        1
      )
      const nextInStock = newFilters.includes('in-stock')
      const nextOnSale = newFilters.includes('on-sale')
      const nextExclusive = newFilters.includes('exclusives')
      trackFilteredCategory({
        filters: newFilters,
        inStock: nextInStock,
        onSale: nextOnSale,
        exclusive: nextExclusive,
      })
      navigateWithDebounce(newUrl)
    },
    [
      currentFilters,
      params.categorySlug,
      canonicalProductSegment,
      sort,
      navigateWithDebounce,
      trackFilteredCategory,
    ]
  )

  const removeFromRoute = useCallback(
    (value: string) => {
      const newFilters = currentFilters.filter(s => s !== value)

      const newUrl = buildFilterUrl(
        params.categorySlug as string,
        canonicalProductSegment,
        newFilters,
        sort,
        1
      )
      const nextInStock = newFilters.includes('in-stock')
      const nextOnSale = newFilters.includes('on-sale')
      const nextExclusive = newFilters.includes('exclusives')
      trackFilteredCategory({
        filters: newFilters,
        inStock: nextInStock,
        onSale: nextOnSale,
        exclusive: nextExclusive,
      })
      navigateWithDebounce(newUrl)
    },
    [
      currentFilters,
      params.categorySlug,
      canonicalProductSegment,
      sort,
      navigateWithDebounce,
      trackFilteredCategory,
    ]
  )

  const toggleProductType = useCallback(
    (key: ProductTypeUrlKey) => {
      const nextKeys = selectedKeys.includes(key)
        ? selectedKeys.filter(k => k !== key)
        : [...selectedKeys, key]
      const segment = canonicalProductTypeFilterSegmentFromSelection(
        nextKeys,
        enabledProductTypes
      )
      const newUrl = buildFilterUrl(
        params.categorySlug as string,
        segment,
        currentFilters,
        sort,
        parsedFilters.page
      )
      trackFilteredCategory({ productTypeSegment: segment })
      navigateWithDebounce(newUrl)
    },
    [
      selectedKeys,
      enabledProductTypes,
      params.categorySlug,
      currentFilters,
      sort,
      parsedFilters.page,
      trackFilteredCategory,
      navigateWithDebounce,
    ]
  )

  const clearProductTypes = useCallback(() => {
    const newUrl = buildFilterUrl(
      params.categorySlug as string,
      'all',
      currentFilters,
      sort,
      parsedFilters.page
    )
    trackFilteredCategory({ productTypeSegment: 'all' })
    navigateWithDebounce(newUrl)
  }, [
    params.categorySlug,
    currentFilters,
    sort,
    parsedFilters.page,
    trackFilteredCategory,
    navigateWithDebounce,
  ])

  const onViewAllProductTypesChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        clearProductTypes()
        return
      }
      if (isAllProductTypes && enabledProductTypes[0]) {
        const first = enabledProductTypes[0]
        const segment = canonicalProductTypeFilterSegmentFromSelection(
          [first],
          enabledProductTypes
        )
        const newUrl = buildFilterUrl(
          params.categorySlug as string,
          segment,
          currentFilters,
          sort,
          parsedFilters.page
        )
        trackFilteredCategory({ productTypeSegment: segment })
        navigateWithDebounce(newUrl)
      }
    },
    [
      isAllProductTypes,
      enabledProductTypes,
      clearProductTypes,
      params.categorySlug,
      currentFilters,
      sort,
      parsedFilters.page,
      trackFilteredCategory,
      navigateWithDebounce,
    ]
  )

  return (
    <div className="rounded p-3 bg-[#fafafa]">
      <h2 className="text-xl border-b pb-2 font-bold">Filter {categoryName}</h2>
      <p className="text-sm text-gray-500 italic pt-2">
        {isUpdating ? (
          <span className="flex items-center gap-1">
            <ArrowPathIcon className="w-4 h-4 animate-spin" />
            Updating products...
          </span>
        ) : (
          <>
            {totalProducts} {sidebarProductLabel}
          </>
        )}
      </p>

      <div className="mt-2 mb-3">
        <label htmlFor="sort" className="font-bold">
          Sort products:
        </label>
        <select
          name="sort"
          id="sort"
          value={sort}
          onChange={e => {
            const nextSort = e.target.value
            setSort(nextSort)
            const newUrl = buildFilterUrl(
              params.categorySlug as string,
              canonicalProductSegment,
              currentFilters,
              nextSort,
              1
            )
            trackFilteredCategory({ sort: nextSort })
            navigateWithDebounce(newUrl)
          }}
          className="block w-full mt-1 rounded border border-gray-300 bg-white py-1 px-2 focus:border-blue-500 focus:outline-none"
        >
          <option value="newest">Newest</option>
          <option value="a-to-z">A to Z</option>
          <option value="price-asc">Price (low to high)</option>
          <option value="price-desc">Price (high to low)</option>
          <option value="best-sellers">Best sellers</option>
          <option value="rating">Customer rating</option>
        </select>
      </div>

      {allowFilterProductType && (
        <div className="mt-2 mb-3">
          <strong className="font-bold">Filter by product type:</strong>
          <div className="mt-2 space-y-1">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="viewAllProductTypes"
                checked={isAllProductTypes}
                onChange={e => onViewAllProductTypesChange(e.target.checked)}
                className="form-checkbox h-4 w-4 text-vi-red"
              />
              <label htmlFor="viewAllProductTypes" className="ml-2 font-medium">
                View all
              </label>
            </div>
            {enabledProductTypes.map(productType => (
              <div key={productType} className="flex items-center">
                <input
                  type="checkbox"
                  id={`productType-${productType}`}
                  checked={
                    !isAllProductTypes && selectedKeys.includes(productType)
                  }
                  onChange={() => toggleProductType(productType)}
                  className="form-checkbox h-4 w-4 text-vi-red"
                />
                <label htmlFor={`productType-${productType}`} className="ml-2">
                  {formatProductTypeFilterSummary(productType)}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      <hr className="my-4" />

      {showStockInFilter && filters.includes('inStock') && (
        <div className="flex items-center mt-1">
          <input
            type="checkbox"
            id="hideOOS"
            checked={inStock}
            onChange={e => {
              if (e.target.checked) {
                addToRoute('in-stock')
              } else {
                removeFromRoute('in-stock')
              }
            }}
            className="form-checkbox h-4 w-4 text-vi-red"
          />
          <label htmlFor="hideOOS" className="ml-2">
            Show in-stock {sidebarProductLabel} first
          </label>
        </div>
      )}

      {filters.includes('discounted') && (
        <div className="flex items-center mt-1">
          <input
            type="checkbox"
            id="onSale"
            checked={onSale}
            onChange={e => {
              if (e.target.checked) {
                addToRoute('on-sale')
              } else {
                removeFromRoute('on-sale')
              }
            }}
            className="form-checkbox h-4 w-4 text-vi-red"
          />
          <label htmlFor="onSale" className="ml-2">
            Show discounted {sidebarProductLabel} only
          </label>
        </div>
      )}

      {filters.includes('exclusive') && (
        <div className="flex items-center mt-1">
          <input
            type="checkbox"
            id="vanishingIncExclusive"
            checked={exclusive}
            onChange={e => {
              if (e.target.checked) {
                addToRoute('exclusives')
              } else {
                removeFromRoute('exclusives')
              }
            }}
            className="form-checkbox h-4 w-4 text-vi-red"
          />
          <label htmlFor="vanishingIncExclusive" className="ml-2">
            Show Vanishing Inc. {sidebarProductLabel} only
          </label>
        </div>
      )}
    </div>
  )
}

interface CategoryFilterProps {
  categoryName: string
  totalProducts: number
  filters?: FilterType[]
  showProductTypeInFilter?: boolean
  showStockInFilter?: boolean
  productTypeInFilter?: string
}
